import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import {
    createResourceCenterModule,
    disableProductionResourceCenter,
    fetchResourceCenter,
    fetchResourceCenters,
    promoteResourceCenter,
    getResourceCenterGuideStepContentForAllStates,
    updateModuleName,
    updateResourceCenterName,
    updateResourceCenterAuthoredLanguage,
    updateModuleSegment,
    saveModuleGuideList,
    deleteResourceCenterVersions,
    deleteModuleFromResourceCenter,
} from '@/utils/resource-center-helper.js';

import {
    fetchTranslationStatus,
    importLocalizationFile,
    clearTranslatedStrings,
    saveRCTranslations,
} from '@/resource-center/resource-center-localization.utils.js';
import {
    createModule,
    getMaxPriority,
    addToResourceCenter,
    addNotificationBubbleToResourceCenter,
    synchronizeModuleWithHomeView,
} from './create-module.utils';
import { createGuide } from '@/guides/helpers/guide-editor.util.js';
import { updateModuleIframeSrc } from '@pendo/one-ui-shared';
import { BuildingBlock, BuildingBlockEditing, BuildingBlockLayouts } from '@pendo/services/BuildingBlocks';
import { createDraftResourceCenter } from './create-resource-center.utils';
import { createAgent, fetchAiAgents, VISITOR_ASSIST_INTEGRATION_PROVIDER } from '@/ai-agents/components/ai-agents.utils';

let loadAllPromise;
let loadStatusPromise;

const resourceCenter = {
    namespaced: true,
    state: {
        map: {},
        error: null,
        localizationStatusMap: {},
        localizationStatusPoller: null,
        fetching: false,
        savingTranslations: false,
        activeAppId: -323232,
        addingModules: false,
        blockResourceCenterChanges: false,
        selectedResourceCenters: null,
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setLocalizationStatusMap (state, { map }) {
            state.localizationStatusMap = map;
        },
        setLocalizationStatusMapAtKey (state, { langCode, status }) {
            state.localizationStatusMap[langCode] = status;
        },
        setLocalizationStatusPoller (state, id) {
            state.localizationStatusPoller = id;
        },
        setMapAtKey (state, { id, resourceCenter }) {
            state.map[id] = resourceCenter;
        },
        deleteMapAtKey (state, { id }) {
            delete state.map[id];
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
        setDraftModule (state, { id, updatedModule }) {
            const index = state.map[id].draft.modules.findIndex((module) => module.id === updatedModule.id);
            state.map[id].draft.modules[index] = updatedModule;
        },
        setDraftResourceCenterAtKey (state, { id, resourceCenter }) {
            state.map[id].draft.homeView = resourceCenter;
        },
        setActiveAppId (state, { appId }) {
            state.activeAppId = appId;
        },
        resetActiveAppId (state) {
            state.activeAppId = -323232;
        },
        setAddingModules (state, { addingModules }) {
            state.addingModules = addingModules;
        },
        setSelectedResourceCenters (state, { resourceCenters }) {
            state.selectedResourceCenters = resourceCenters;
        },
        clearLocalizationStatusPoller (state) {
            state.localizationStatusPoller = null;
        },
        clearSelectedResourceCenters (state) {
            state.selectedResourceCenters = null;
        },
        setResourceCenterChanges (state, { block }) {
            state.blockResourceCenterChanges = block;
        },
        setSavingTranslations (state, { saving }) {
            state.savingTranslations = saving;
        },
    },
    getters: {
        list: (state) => values(state.map),
        isProcessingTranslation: (state) => (langCode) => !!state.localizationStatusMap[langCode],
        isProcessingTranslations: (state) => {
            return values(state.localizationStatusMap).some((jobRunning) => jobRunning);
        },
        isSavingTranslations: (state) => state.savingTranslations,
        getResourceCenterGuides: (state, getters, rootState, rootGetters) =>
            filter(rootGetters['guides/list'], (guide) => guide && guide.attributes && guide.attributes.resourceCenter),
        getModuleParent: (state, getters) => ({ module }) =>
            getters.getResourceCenterGuides.find(
                (guide) =>
                    !!guide.attributes.resourceCenter.children.find((childGuideId) => childGuideId === module.id),
            ),
        draftResourceCenters: (state, getters, rootState, rootGetters) => ({ appId }) =>
            rootGetters['guides/list'].filter(
                (guide) =>
                    get(guide, 'attributes.resourceCenter.isTopLevel') &&
                    guide.appId === appId &&
                    guide.state === 'draft',
            ),
        allActivePublicResourceCenters: (state, getters) => {
            return getters.list.filter(
                (rc) => rc.public && !rc.public.isDisabled && get(rc.public, 'homeView.state') === 'public',
            );
        },
        getChildGuides: (state, getters, rootState, rootGetters) => ({ parentGuide }) => {
            return parentGuide.attributes.resourceCenter.children.reduce((acc, guideId) => {
                const guide = rootGetters['guides/getGuideById'](guideId);
                if (!guide) return acc;

                return acc.concat(guide);
            }, []);
        },
        getBuildingBlocksForExistingModules: (state) => ({ resourceCenterId }) => {
            return get(state.map[resourceCenterId], 'draft.modules', []).map((module) =>
                get(module, 'steps[0].buildingBlocks'),
            );
        },
        resourceCenterById: (state) => (id) => state.map[id],
        draftResourceCenterList: (state, getters) => {
            return get(getters, 'list', []).map((rc) => get(rc, 'draft', {}));
        },
        draftResourceCenterMap: (state, getters) => {
            return keyBy(getters.draftResourceCenterList, 'homeView.appId');
        },
    },
    actions: {
        loadAll ({ commit, rootGetters }, { reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) return loadAllPromise;

            commit('setFetching', { fetching: true });

            const integratedApps = rootGetters['apps/onlyIntegratedApps'];

            loadAllPromise = fetchResourceCenters(integratedApps)
                .then(({ data: resourceCenters }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMap', { map: keyBy(resourceCenters, 'id') });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });

            return loadAllPromise;
        },
        loadOne ({ commit, state }, { reFetch = false, ...payload } = {}) {
            const { id } = payload;

            // Check if loaded in state unless force reFetch is true
            if (!reFetch && !isUndefined(state.map[id])) {
                return Promise.resolve();
            }

            commit('setFetching', { fetching: true });

            return fetchResourceCenter(id)
                .then((resourceCenter) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', { id, resourceCenter });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async loadLocalizationStatus ({ state, commit }, { homeViewId }) {
            // Don't start another poller if we already have one watching the status values.
            if (loadStatusPromise) return loadStatusPromise;

            const shouldStopPolling = ({ response, error }) => {
                if (!state.localizationStatusPoller) return true;
                if (error) return false;

                const { data } = response;
                const statusMap = {
                    // New languages aren't immediately picked up by the status endpoint so keep
                    // previous entries in the map and overwrite them as updates occur.
                    ...state.localizationStatusMap,
                    ...Object.entries(data).reduce((statuses, [langCode, status]) => {
                        statuses[langCode] = !!status.jobRunning;

                        return statuses;
                    }, {}),
                };
                commit('setLocalizationStatusMap', { map: statusMap });

                // Keep polling until all translation jobs have completed.
                return Object.values(statusMap).every((jobRunning) => !jobRunning);
            };

            commit('setLocalizationStatusPoller', homeViewId);
            loadStatusPromise = fetchTranslationStatus(homeViewId, shouldStopPolling);

            try {
                await loadStatusPromise;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                loadStatusPromise = null;
            }
        },
        promoteResourceCenter ({ commit }, { homeViewId, fromState, toState }) {
            commit('setFetching', { fetching: true });

            return promoteResourceCenter(homeViewId, fromState, toState)
                .then((resourceCenter) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: resourceCenter.id,
                        resourceCenter,
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        updateResourceCenterName ({ commit }, { homeViewId, name }) {
            commit('setFetching', { fetching: true });

            return updateResourceCenterName(homeViewId, name)
                .then((resourceCenter) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: resourceCenter.id,
                        resourceCenter,
                    });

                    // update the guide we have changed, within the guide store
                    const guide = resourceCenter.draft.homeView;
                    commit('guides/updateGuidesMap', { guide }, { root: true });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async updateModuleIframeSrc ({ commit, dispatch }, { homeViewId, module, src }) {
            commit('setError', { error: null });
            commit('setFetching', { fetching: true });

            try {
                const updatedModule = updateModuleIframeSrc(module, src);
                await dispatch('updateModule', { homeViewId, updatedModule });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
            }
        },
        async updateModule ({ state, commit, dispatch }, { homeViewId, updatedModule }) {
            synchronizeModuleWithHomeView(state.map[homeViewId], updatedModule);

            let { buildingBlocks: bb } = updatedModule.steps[0];

            // Different utilities store the updated building blocks as either JSON or string.
            // No standard exists so we need to check for either.
            try {
                bb = has(bb, 'id') ? bb : JSON.parse(bb);
            } catch (error) {
                commit('setError', { error });

                return;
            }

            // Update the DOM representation to match the updated building blocks
            const bbDom = BuildingBlock.buildingBlocksToDom(bb);
            BuildingBlockEditing.removeEditingIdsFromDom(bbDom);

            updatedModule.steps[0].buildingBlocks = JSON.stringify(bb);
            updatedModule.steps[0].dom = JSON.stringify(bbDom);

            await createResourceCenterModule(homeViewId, updatedModule);
            // Loading the entire RC is overkill:
            // we get the updated module from the createResourceCenterModule API call.
            // But reloading the entire RC is consistent with how we handle other module updates.
            await dispatch('loadOne', { reFetch: true, id: homeViewId });
        },
        async updateResourceCenterAuthoredLanguage ({ commit }, { homeViewId, authoredLanguage }) {
            commit('setFetching', { fetching: true });

            let resourceCenter;

            try {
                resourceCenter = await updateResourceCenterAuthoredLanguage(homeViewId, authoredLanguage);
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }

            commit('setFetching', { fetching: false });
            commit('setMapAtKey', { id: resourceCenter.id, resourceCenter });

            // update the underlying guide that represents the homeView
            const { homeView } = resourceCenter.draft;
            commit('guides/addGuideToMap', { guide: homeView }, { root: true });

            // The authoredLanguage prop for each module will have changed so we need to update
            // the copy of the modules in the guide store as well
            const { modules } = resourceCenter.draft;
            modules.forEach((module) => {
                commit('guides/addGuideToMap', { guide: module }, { root: true });
            });
        },
        async updateModuleName ({ commit }, { homeViewId, moduleId, name }) {
            commit('setFetching', { fetching: true });

            try {
                const resourceCenter = await updateModuleName(homeViewId, moduleId, name);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', {
                    id: resourceCenter.id,
                    resourceCenter,
                });
                // update the guide we have changed, within the guide store
                const guide = resourceCenter.draft.modules.find(({ id }) => id === moduleId);
                commit('guides/updateGuidesMap', { guide }, { root: true });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async saveModuleGuideList ({ commit, dispatch }, { homeViewId, moduleId, guideList }) {
            commit('setFetching', { fetching: true });

            try {
                await saveModuleGuideList(homeViewId, moduleId, guideList);
                await dispatch('loadLocalizationStatus', { homeViewId });
                await dispatch('loadLocalizedResourceCenterContent', { id: homeViewId });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        // BE will eventually enable isDisabled for other versions and we can use version prop to handle that
        disableResourceCenterVersion ({ commit }, { homeViewId, isDisabled = true }) {
            commit('setFetching', { fetching: true });

            return disableProductionResourceCenter(homeViewId, isDisabled)
                .then((resourceCenter) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: resourceCenter.id,
                        resourceCenter,
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async updateModuleSegment ({ commit }, { homeViewId, id, audience, audienceUiHint } = {}) {
            commit('setFetching', { fetching: true });
            commit('setError', { error: null });

            try {
                const resourceCenter = await updateModuleSegment(homeViewId, id, audience, audienceUiHint);
                commit('setFetching', { fetching: false });
                commit('setMapAtKey', {
                    id: resourceCenter.id,
                    resourceCenter,
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
            }
        },
        deleteOne ({ commit }, { homeViewId }) {
            commit('setFetching', { fetching: true });

            return deleteResourceCenterVersions(homeViewId)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapAtKey', { id: homeViewId });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async loadLocalizedResourceCenterContent ({ commit, dispatch }, { id }) {
            const resourceCenter = await fetchResourceCenter(id);
            await getResourceCenterGuideStepContentForAllStates(resourceCenter);

            commit('setMapAtKey', { id, resourceCenter });

            // We reloadGuides (which bypasses the cache) so that guides module and the resource
            // center module stay in sync. This is needed since the underlying guides in the the RC
            // are duplicated in the guides module. This is super inefficient, but it's how we have to
            // manage keeping guides and resource center modules in sync for the time being.
            await dispatch('guides/reloadGuides', {}, { root: true });
        },
        async importResourceCenterLocalizationFile ({ commit, rootState, dispatch }, { id, langCode, file }) {
            // importLocalizationFile bypasses the http interceptor we use to dynamically
            // inject the subId, hence why we need to get it from state and pass it in here
            const subscriptionId = get(rootState, 'subscription.active.id');
            commit('setFetching', { fetching: true });
            commit('setLocalizationStatusMapAtKey', { langCode, status: true });

            try {
                await importLocalizationFile(subscriptionId, id, langCode, file);
                await dispatch('loadLocalizationStatus', { homeViewId: id });
                await dispatch('loadLocalizedResourceCenterContent', { id });
            } catch (error) {
                commit('setError', { error });
                commit('setLocalizationStatusMapAtKey', { langCode, status: false });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async disableDraftModule ({ state, commit, dispatch, rootGetters }, { homeViewId, moduleGuideToDisable }) {
            // If a staged version of the RC does not exist, we'll actually delete the module altogether
            if (isNull(state.map[homeViewId].staged)) {
                await deleteModuleFromResourceCenter(homeViewId, moduleGuideToDisable.id);
                await dispatch('guides/reloadGuides', {}, { root: true });
                await dispatch('loadOne', { reFetch: true, id: homeViewId });

                return;
            }

            // If a staged version of the RC exists, we simply patch the guide representing the module
            // and mark it as disabled. Upon promotion, the BE will handle removing the module from the
            // resource center.
            moduleGuideToDisable = cloneDeep(moduleGuideToDisable);
            moduleGuideToDisable.state = 'disabled';

            // Patching the guide re-fetches the entire guide from the BE, purging the content (building blocks).
            // Since we're only disabling the module--not deleting it--we re-build the content
            // So that the UI can still display the full module.
            await dispatch(
                'guides/patchGuide',
                { guide: moduleGuideToDisable, fieldsToPatch: ['state'], buildContent: true },
                { root: true },
            );
            const updatedModule = rootGetters['guides/getGuideById'](moduleGuideToDisable.id);
            commit('setDraftModule', { id: homeViewId, updatedModule });
        },
        clearResourceCenterTranslationStrings ({ dispatch, commit }, { homeViewId, langCode }) {
            commit('setFetching', { fetching: true });

            return clearTranslatedStrings(homeViewId, langCode)
                .then((resourceCenter) => {
                    return (
                        getResourceCenterGuideStepContentForAllStates(resourceCenter)
                            // We reloadGuides (which bypasses the cache) so that guides module and the resource
                            // center module stay in sync. This is needed since the underlying guides in the the RC
                            // are duplicated in the guides module. This is super inefficient, but it's how we have to
                            // manage keeping guides and resource center modules in sync for the time being.
                            .then(() => dispatch('guides/reloadGuides', {}, { root: true }))
                            .then(() => {
                                commit('setMapAtKey', {
                                    id: homeViewId,
                                    resourceCenter,
                                });
                                commit('setFetching', { fetching: false });
                            })
                    );
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        async createDraftResourceCenter ({ state, dispatch, rootGetters }) {
            try {
                const maxPriority = rootGetters['guides/maxPriority'];
                const resourceGuideObj = await createGuide({ guideType: 'building-block', maxPriority });
                const resourceCenter = await createDraftResourceCenter({
                    resourceCenter: resourceGuideObj,
                    activeAppId: state.activeAppId,
                });
                await dispatch('guides/saveGuide', { guide: resourceCenter }, { root: true });

                return resourceCenter;
            } catch (error) {
                throw error;
            }
        },
        async createResourceCenterModules (
            { commit, rootGetters, dispatch, state, getters },
            { modulesToAdd, resourceCenterId },
        ) {
            const draftHomeView = rootGetters['guides/getGuideById'](resourceCenterId);
            let hasAnnouncementsModule = false;
            let updateResourceCenter;

            try {
                await dispatch('guides/loadAll', { reFetch: true }, { root: true });
                const buildingBlocksForExistingModules = getters.getBuildingBlocksForExistingModules({
                    resourceCenterId: draftHomeView.id,
                });
                const maxPriority = getMaxPriority(rootGetters['guides/list']);
                // create the modules in parallel. order isn't important until we add them to a resource center
                const newModules = await Promise.all(
                    modulesToAdd.map((moduleToAdd) =>
                        createModule(draftHomeView, moduleToAdd, buildingBlocksForExistingModules, maxPriority),
                    ),
                );

                const visitorAssistNewModule = newModules.find(
                    ({ moduleConfig }) => moduleConfig.integrationProvider === VISITOR_ASSIST_INTEGRATION_PROVIDER,
                );
                let createAgentPromise;
                if (visitorAssistNewModule) {
                    const existingAgents = await fetchAiAgents();
                    const visitorAssistAgentExists = existingAgents.some(
                        (agent) => agent.type === VISITOR_ASSIST_INTEGRATION_PROVIDER,
                    );
                    if (!visitorAssistAgentExists) {
                        const { module } = visitorAssistNewModule;
                        const { displayName } = rootGetters['apps/getAppById'](module.appId);
                        createAgentPromise = createAgent({
                            name: `${displayName} ${module.name}`,
                            description: 'Visitor Assist Agent',
                            appId: module.appId,
                            type: VISITOR_ASSIST_INTEGRATION_PROVIDER,
                            conversationsEnabled: true,
                        });
                    }
                }

                // order matters when adding modules to a resource center so we do that sequentially below
                for (const newModule of newModules) {
                    const { module, moduleConfig } = newModule;
                    // added an ignore for this rule because we actually do want to make these requests sequentially
                    // eslint-disable-next-line no-await-in-loop
                    updateResourceCenter = await addToResourceCenter(module, moduleConfig, resourceCenterId);

                    if (
                        get(module, 'attributes.resourceCenter.moduleId') ===
                        BuildingBlockLayouts.moduleIds.announcements
                    ) {
                        hasAnnouncementsModule = true;
                    }

                    // Add the newly created module (guide) to the Guides Vuex module
                    commit('guides/addGuideToMap', { guide: module }, { root: true });
                }

                if (hasAnnouncementsModule) {
                    const draftHomeViewClone = await addNotificationBubbleToResourceCenter(
                        updateResourceCenter.draft.homeView,
                    );
                    await dispatch(
                        'guides/patchGuideAndSteps',
                        {
                            guide: draftHomeViewClone,
                            guideFieldsToPatch: [
                                'attributes',
                                'audience',
                                'audienceUiHint',
                                'launchMethod',
                                'state',
                                'name',
                            ],
                            stepFieldsToPatch: [
                                'buildingBlocks',
                                'dom',
                                'content',
                                'contentJs',
                                'contentCss',
                                'attributes',
                                'elementPathRule',
                                'pageId',
                            ],
                        },
                        { root: true },
                    );
                }

                // At this point, the modules are added and the resource center has been modified so re-fetch
                // this gives us the up to date resource center complete with step content for the homeView
                // and modules
                await dispatch('loadOne', { id: resourceCenterId, reFetch: true });

                // pull the draft home view of the updated resource center and update the underlying guide
                const updatedDraftHomeView = state.map[updateResourceCenter.id].draft.homeView;
                commit('guides/addGuideToMap', { guide: updatedDraftHomeView }, { root: true });

                if (createAgentPromise) {
                    await createAgentPromise.catch(() => {
                        throw new Error('Failed to create visitor assist agent');
                    });
                }
            } catch (error) {
                throw error;
            }
        },
        updateAddingModules ({ commit }, { addingModules }) {
            commit('setAddingModules', { addingModules });
        },
        async importRCTranslation ({ commit, dispatch }, { homeViewId, langCode, translations }) {
            commit('setSavingTranslations', { saving: true });
            try {
                await saveRCTranslations(homeViewId, langCode, translations);
                await dispatch('loadLocalizedResourceCenterContent', { id: homeViewId });
            } finally {
                commit('setSavingTranslations', { saving: false });
            }
        },
        blockRcChanges ({ commit }, block) {
            commit('setResourceCenterChanges', { block });
        },
    },
};

export default resourceCenter;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
    loadStatusPromise = null;
}

export function __getLoadAllPromiseForUnitTests () {
    return { loadAllPromise, loadStatusPromise };
}
